import { useEffect, useMemo, useState } from 'react'
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
  type SubmitHandler,
} from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Textarea,
} from '@clouway/ui'
import { Plus, Trash2 } from 'lucide-react'

import { VendorModelSelector, VendorSelector, useVendorModels } from '@/features/vendors'
import {
  ANALOG_PORTS,
  AnalogInputEditForm,
  DigitalInputEditForm,
  SensorEditForm,
  TrackingObjectTypeSelector,
  type AnalogInputFormValues,
  type DigitalInputFormValues,
  type SensorFormValues,
} from '@/features/trackingObjects'
import { UnsavedChangesGuard } from '@/components/UnsavedChangesGuard'
import { emptyTemplateForm } from '../transforms'
import type { ConfigTemplateFormValues } from '../types'

const SENSOR_PORTS = ['0', '1', '2', '3']
const SENSOR_SECTIONS = [
  { key: 'batteryIndication', label: 'trackingObjects.configs.batteryIndication', inverse: true },
  { key: 'batteryVoltage', label: 'trackingObjects.configs.batteryVoltage', inverse: false },
  { key: 'magnetStatus', label: 'trackingObjects.configs.magnetStatus', inverse: true },
  { key: 'temperature', label: 'trackingObjects.configs.temperature', inverse: false },
] as const

function toNum(value: string): number | null {
  if (value.trim() === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

// Only name + device are gated at step 1; arrays/commands pass through.
const buildSchema = (msg: string) =>
  z.object({
    name: z.string().min(1, msg),
    device: z.object({
      type: z.string().min(1, msg),
      vendorId: z.string().min(1, msg),
      modelId: z.string().min(1, msg),
      modelName: z.string(),
    }),
    smsCommands: z.string(),
    gprsCommands: z.string(),
    analogPortConfigs: z.array(z.any()),
    digitalPortConfigs: z.array(z.any()),
    sensorConfiguration: z.object({
      temperature: z.array(z.any()),
      magnetStatus: z.array(z.any()),
      batteryIndication: z.array(z.any()),
      batteryVoltage: z.array(z.any()),
    }),
  })

function DeviceStep() {
  const { t } = useTranslation('feature')
  const { control, resetField, setValue } = useFormContext<ConfigTemplateFormValues>()
  const vendorId = useWatch({ control, name: 'device.vendorId' })
  const modelId = useWatch({ control, name: 'device.modelId' })
  const { data: models = [] } = useVendorModels(vendorId ?? '')

  // Reset the model when the vendor changes — the mobile reset the wrong field
  // name ('deviceModelId'); the correct path is device.modelId.
  useEffect(() => {
    resetField('device.modelId')
    resetField('device.modelName')
  }, [vendorId, resetField])

  // Keep the (wire-required) model name in sync with the chosen model id.
  useEffect(() => {
    const found = models.find((m) => m.id === modelId)
    if (found) setValue('device.modelName', found.name)
  }, [modelId, models, setValue])

  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name="name"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel htmlFor="tpl-name">{t('configTemplates.form.templateName')}</FieldLabel>
            <Input id="tpl-name" {...field} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />
      <Controller
        control={control}
        name="device.type"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('configTemplates.form.objectType')}</FieldLabel>
            <TrackingObjectTypeSelector value={field.value} onChange={field.onChange} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />
      <Controller
        control={control}
        name="device.vendorId"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('configTemplates.form.vendor')}</FieldLabel>
            <VendorSelector value={field.value} onChange={field.onChange} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />
      <Controller
        control={control}
        name="device.modelId"
        render={({ field, fieldState }) => (
          <Field data-invalid={!!fieldState.error}>
            <FieldLabel>{t('configTemplates.form.model')}</FieldLabel>
            <VendorModelSelector vendorId={vendorId} value={field.value} onChange={field.onChange} />
            {fieldState.error && <FieldError>{fieldState.error.message}</FieldError>}
          </Field>
        )}
      />
    </div>
  )
}

function AnalogsStep() {
  const { t } = useTranslation('feature')
  const { control } = useFormContext<ConfigTemplateFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'analogPortConfigs' })
  const [adding, setAdding] = useState(false)
  const used = new Set(fields.map((f) => f.port))
  const available = ANALOG_PORTS.filter((p) => !used.has(p))

  const onAdd = (v: AnalogInputFormValues) => {
    append({
      port: v.port,
      type: v.type,
      unit: v.unit,
      noise: v.noise,
      min: toNum(v.min),
      max: toNum(v.max),
      multiplier: toNum(v.multiplier),
      readings: [],
    })
    setAdding(false)
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.map((field, index) => (
        <Card key={field.id}>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">
                {t(`trackingObjects.config.analogPorts.${field.port}` as 'trackingObjects.config.analogPorts.analog_1')}
              </CardTitle>
              <Button size="icon" variant="ghost" aria-label="delete" onClick={() => remove(index)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          </CardHeader>
        </Card>
      ))}
      {adding ? (
        <AnalogInputEditForm availablePorts={available} onSubmit={onAdd} onCancel={() => setAdding(false)} />
      ) : (
        <Button variant="outline" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          {t('trackingObjects.config.analogs')}
        </Button>
      )}
    </div>
  )
}

function DigitalsStep() {
  const { t } = useTranslation('feature')
  const { control } = useFormContext<ConfigTemplateFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: 'digitalPortConfigs' })
  const [adding, setAdding] = useState(false)
  const used = new Set(fields.map((f) => String(f.port)))
  const available = ['0', '1', '2', '3'].filter((p) => !used.has(p))

  const onAdd = (v: DigitalInputFormValues) => {
    append({ port: Number(v.port), type: v.type, working: v.working, inverse: v.inverse })
    setAdding(false)
  }

  return (
    <div className="flex flex-col gap-3">
      {fields.map((field, index) => (
        <Card key={field.id}>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">
                {t(`trackingObjects.config.digitalInputTypes.${field.type}` as 'trackingObjects.config.digitalInputTypes.door')}{' '}
                · {field.port}
              </CardTitle>
              <Button size="icon" variant="ghost" aria-label="delete" onClick={() => remove(index)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          </CardHeader>
        </Card>
      ))}
      {adding ? (
        <DigitalInputEditForm availablePorts={available} onSubmit={onAdd} onCancel={() => setAdding(false)} />
      ) : (
        <Button variant="outline" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          {t('trackingObjects.config.digitals')}
        </Button>
      )}
    </div>
  )
}

function SensorTypeSection({ sectionKey, label, inverse }: { sectionKey: (typeof SENSOR_SECTIONS)[number]['key']; label: string; inverse: boolean }) {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { control } = useFormContext<ConfigTemplateFormValues>()
  const { fields, append, remove } = useFieldArray({ control, name: `sensorConfiguration.${sectionKey}` })
  const [adding, setAdding] = useState(false)
  const used = new Set(fields.map((f) => String(f.id)))
  const available = SENSOR_PORTS.filter((p) => !used.has(p))

  const onAdd = (v: SensorFormValues) => {
    const mult = Number(v.multiplier)
    append({
      id: Number(v.id),
      title: v.title,
      unit: v.unit,
      multiplier: v.multiplier.trim() === '' || Number.isNaN(mult) ? 0 : mult,
      enabled: v.enabled,
      inverse: v.inverse,
    })
    setAdding(false)
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold">{tCommon(label as 'trackingObjects.configs.temperature')}</h3>
      {fields.map((field, index) => (
        <Card key={field.id}>
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">
                {field.title} · {t('sensorForm.port')} {field.id}
              </CardTitle>
              <Button size="icon" variant="ghost" aria-label="delete" onClick={() => remove(index)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          </CardHeader>
        </Card>
      ))}
      {adding ? (
        <SensorEditForm
          availablePorts={available}
          showInverse={inverse}
          onSubmit={onAdd}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <Button variant="outline" size="sm" disabled={available.length === 0} onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          {tCommon('trackingObjects.configs.newSensor')}
        </Button>
      )}
    </section>
  )
}

function SensorsStep() {
  return (
    <div className="flex flex-col gap-6">
      {SENSOR_SECTIONS.map((s) => (
        <SensorTypeSection key={s.key} sectionKey={s.key} label={s.label} inverse={s.inverse} />
      ))}
    </div>
  )
}

function CommandsStep() {
  const { t } = useTranslation('feature')
  const { control } = useFormContext<ConfigTemplateFormValues>()
  return (
    <div className="flex flex-col gap-4">
      <Controller
        control={control}
        name="gprsCommands"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="gprs">{t('configTemplates.form.gprsCommands')}</FieldLabel>
            <Textarea id="gprs" rows={5} placeholder={t('configTemplates.form.textareaPlaceholder')} {...field} />
          </Field>
        )}
      />
      <Controller
        control={control}
        name="smsCommands"
        render={({ field }) => (
          <Field>
            <FieldLabel htmlFor="sms">{t('configTemplates.form.smsCommands')}</FieldLabel>
            <Textarea id="sms" rows={5} placeholder={t('configTemplates.form.textareaPlaceholder')} {...field} />
          </Field>
        )}
      />
    </div>
  )
}

export function ConfigTemplateWizard({
  initialValues,
  onSubmit,
}: {
  initialValues?: ConfigTemplateFormValues
  onSubmit: SubmitHandler<ConfigTemplateFormValues>
}) {
  const { t } = useTranslation('feature')
  const schema = useMemo(() => buildSchema(t('network.form.required')), [t])
  const form = useForm<ConfigTemplateFormValues>({
    resolver: zodResolver(schema),
    defaultValues: initialValues ?? emptyTemplateForm(),
  })
  const [step, setStep] = useState(1)

  const goNext = async () => {
    // Gate leaving the device step until name + device validate.
    if (step === 1) {
      const ok = await form.trigger(['name', 'device'])
      if (!ok) return
    }
    setStep((s) => Math.min(s + 1, 5))
  }

  return (
    <FormProvider {...form}>
      <form
        className="mx-auto flex w-full max-w-2xl flex-col gap-4"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <UnsavedChangesGuard when={form.formState.isDirty && !form.formState.isSubmitSuccessful} />
        <p className="text-muted-foreground text-sm" data-testid="wizard-step">
          {step} / 5
        </p>

        {step === 1 && <DeviceStep />}
        {step === 2 && <AnalogsStep />}
        {step === 3 && <DigitalsStep />}
        {step === 4 && <SensorsStep />}
        {step === 5 && <CommandsStep />}

        <div className="flex justify-end gap-2">
          {step > 1 && (
            <Button type="button" variant="outline" onClick={() => setStep((s) => Math.max(s - 1, 1))}>
              {t('configTemplates.form.backStep')}
            </Button>
          )}
          {step < 5 && (
            <Button type="button" onClick={goNext}>
              {t('configTemplates.form.nextStep')}
            </Button>
          )}
          {step === 5 && <Button type="submit">{t('configTemplates.form.submit')}</Button>}
        </div>
      </form>
    </FormProvider>
  )
}
